import React, { useState } from 'react';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { Link } from 'react-router';
import { SkeletonCard } from '../components/ui/Skeleton';
import { EmptyServices, ErrorState } from '../components/ui/EmptyState';
import { useAllServices } from '../hooks/useServices';
import { useLanguage } from '../lang/LangContext';
import { KEYS } from '../lang/keys';

const categoryBorder = {
    electricity: 'border-l-category-electricity',
    natural_gas: 'border-l-category-gas',
    water: 'border-l-category-water',
    phone: 'border-l-category-phone',
    internet: 'border-l-category-internet',
    other: 'border-l-surface-border',
};

const categoryDot = {
    electricity: 'bg-category-electricity',
    natural_gas: 'bg-category-gas',
    water: 'bg-category-water',
    phone: 'bg-category-phone',
    internet: 'bg-category-internet',
    other: 'bg-text-muted',
};

const typeLabels = {
    electricity: 'Electricity',
    natural_gas: 'Natural Gas',
    water: 'Water',
    phone: 'Phone',
    internet: 'Internet',
    other: 'Other',
};

const CategorySection = ({ category, services }) => {
    const [open, setOpen] = useState(true);
    const { t } = useLanguage();
    const typeLabelsLocalized = {
        electricity: t(KEYS.TYPE_ELECTRICITY),
        natural_gas: t(KEYS.TYPE_NATURAL_GAS),
        water: t(KEYS.TYPE_WATER),
        phone: t(KEYS.TYPE_PHONE),
        internet: t(KEYS.TYPE_INTERNET),
        other: t(KEYS.TYPE_OTHER),
    };
    const key = category.toLowerCase();
    const border = categoryBorder[key] || categoryBorder.other;
    const dot = categoryDot[key] || categoryDot.other;

    return (
        <div className={`border-l-2 ${border} rounded-r-lg overflow-hidden animate-appear`}>
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center gap-2 px-4 py-2.5 bg-surface-raised hover:bg-surface-overlay transition-colors text-left"
            >
                {open ? (
                    <ChevronDown className="w-3.5 h-3.5 text-text-muted" />
                ) : (
                    <ChevronRight className="w-3.5 h-3.5 text-text-muted" />
                )}
                <span className={`w-2 h-2 rounded-full ${dot}`} />
                <span className="text-xs font-medium text-text-secondary uppercase tracking-wider">
                    {typeLabelsLocalized[category] || category}
                </span>
                <span className="text-[10px] text-text-muted ml-auto">
                    {services.length}
                </span>
            </button>
            {open && (
                <div className="divide-y divide-surface-border bg-surface-raised">
                    {services.map((service) => (
                        <Link
                            key={service.uid}
                            to={`/services/${service.uid}`}
                            className="flex items-center justify-between px-4 py-2.5 pl-11 hover:bg-surface-overlay transition-colors group"
                        >
                            <div className="flex-1 min-w-0 mr-3">
                                <span className="text-sm text-text-primary truncate block">
                                    {service.name}
                                </span>
                                {service.description && (
                                    <p className="text-xs text-text-muted mt-0.5 truncate">
                                        {service.description}
                                    </p>
                                )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                {service.supplierName && (
                                    <span className="text-xs text-text-muted">
                                        {service.supplierName}
                                    </span>
                                )}
                                <ExternalLink className="w-3 h-3 text-text-muted group-hover:text-accent transition-colors" />
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
};

const Services = () => {
    const { data: services = [], isLoading: loading, error, refetch } = useAllServices();
    const { t } = useLanguage();

    // Group services by type
    const grouped = services.reduce((acc, svc) => {
        const cat = svc.type || 'other';
        (acc[cat] = acc[cat] || []).push(svc);
        return acc;
    }, {});

    return (
        <div className="space-y-5">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-semibold text-text-primary">
                    {t(KEYS.SERVICES_TITLE)}
                </h1>
                <p className="text-sm text-text-muted mt-0.5">
                    {t(KEYS.SERVICES_SUBTITLE)}
                </p>
            </div>

            {/* Content */}
            {loading ? (
                <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                        <SkeletonCard key={i} />
                    ))}
                </div>
            ) : error ? (
                <ErrorState message={error?.message || t(KEYS.SERVICES_ERROR_LOAD)} onRetry={refetch} />
            ) : services && services.length > 0 ? (
                <div className="space-y-3">
                    {Object.entries(grouped).map(([category, items]) => (
                        <CategorySection key={category} category={category} services={items} />
                    ))}
                </div>
            ) : (
                <EmptyServices />
            )}
        </div>
    );
};

export default Services;
